import { useOptimisticUpdateQuestionnaire } from '@/api/optimistic-update-questionnaire.hook'
import { QuestionnaireStatus } from '@/gen/questionnaire/common/v1/questionnaire_status_pb'

import { questionnaireStatusLabel } from '@/const/label'

import { useTrackCallback } from '@/lib/analytics/events'
import { UrlParam } from '@/lib/url/url-param.hook'
import { buildPreviewQuestionnaireUrl, buildQuestionnairePortalUrl } from '@/lib/url/urls'

import { useQuestionnaireInviteRecipient } from '@/pages/assessment/questionnaire/use-questionnaire-invite-recipient'

import { useToast } from '@/components/lemmala-design-system/toast/use-toast'

import type { SmartEvidenceRequestBannerProps } from './types'

export const useSmartEvidenceRequest = ({
  assessmentId,
  company,
  smartEvidenceQuestionnaire,
}: SmartEvidenceRequestBannerProps) => {
  const companyId = company.id
  const companyName = company.profile?.name ?? ''
  const recipientsEmails = company.recipientsEmails
  const { toast } = useToast()
  const { mutateAsync: updateQuestionnaire, isPending: isUpdating } =
    useOptimisticUpdateQuestionnaire(companyId, assessmentId)

  const { InviteRecipientModal, openInviteRecipientModal } = useQuestionnaireInviteRecipient({
    company,
  })

  const trackPreviewCallback = useTrackCallback('third-party.smart-evidence-request.preview')
  const trackPublishCallback = useTrackCallback('third-party.smart-evidence-request.publish')
  const trackUnpublishCallback = useTrackCallback('third-party.smart-evidence-request.unpublish')

  const getPreviewUrl = () => {
    if (!smartEvidenceQuestionnaire?.id) return undefined
    return buildPreviewQuestionnaireUrl(smartEvidenceQuestionnaire.id)
  }
  const handleTrackPreview = () => {
    if (!smartEvidenceQuestionnaire?.id) return

    trackPreviewCallback({
      assessmentId,
      companyId,
      companyName,
      questionnaireId: smartEvidenceQuestionnaire.id,
    })
  }

  const handleCopyLink = async () => {
    if (!smartEvidenceQuestionnaire?.id) return

    const url = buildQuestionnairePortalUrl(
      companyId,
      {
        [UrlParam.RECIPIENT_QUESTIONNAIRE]: smartEvidenceQuestionnaire.id,
      },
      { absolute: true },
    )
    await navigator.clipboard.writeText(url)

    toast({
      title: 'Link copied',
      description: 'The questionnaire link has been copied to your clipboard',
      variant: 'success',
    })
  }

  const handlePublish = async () => {
    if (!smartEvidenceQuestionnaire?.id) return

    if (recipientsEmails.length === 0) {
      toast({
        title: 'Cannot publish',
        description: 'You must add at least one recipient before publishing this questionnaire',
        variant: 'destructive',
      })
      return
    }

    try {
      const previousStatus = questionnaireStatusLabel[smartEvidenceQuestionnaire.status]

      await updateQuestionnaire({
        questionnaireId: smartEvidenceQuestionnaire.id,
        status: QuestionnaireStatus.ONGOING,
      })

      trackPublishCallback({
        questionnaireId: smartEvidenceQuestionnaire.id,
        assessmentId,
        companyId,
        companyName,
        previousStatus,
      })

      if (smartEvidenceQuestionnaire.status === QuestionnaireStatus.SUBMITTED) {
        toast({
          title: 'Smart evidence request reopened',
          description: 'Recipients have been notified',
          variant: 'success',
        })
      } else {
        toast({
          title: 'Smart evidence request was published successfully',
          variant: 'success',
        })
      }
    } catch (error) {
      toast({
        title: 'Failed to publish smart evidence request',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    }
  }

  const handleUnpublish = async () => {
    if (!smartEvidenceQuestionnaire?.id) return

    try {
      await updateQuestionnaire({
        questionnaireId: smartEvidenceQuestionnaire.id,
        status: QuestionnaireStatus.DRAFT,
      })

      trackUnpublishCallback({
        questionnaireId: smartEvidenceQuestionnaire.id,
        assessmentId,
        companyId,
        companyName,
      })

      toast({
        title: 'Smart evidence request was unpublished successfully',
        variant: 'success',
      })
    } catch (error) {
      toast({
        title: 'Failed to unpublish smart evidence request',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      })
    }
  }

  return {
    isUpdating,
    getPreviewUrl,
    handleTrackPreview,
    handleCopyLink,
    handlePublish,
    handleUnpublish,
    InviteRecipientModal,
    openInviteRecipientModal,
  }
}
